// ============================================================================
// v4 — OVERVIEW: a RevenuePulse-style hub summarizing every screen.
// ============================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { v4data, TIER, GOALS } from './v4data';
import { AreaChart, Donut, Ring, HBar, Spark } from './viz';
import { PageHeader, DatePill, Card, StatCard, Insight, Pill, ChannelIcon, fmt$, fmt$2, fmtK, fmtN, fmtX, pct, roasTone, toneColor } from './v4ui';
import MarkPopup from './MarkPopup';
import './dashboard.scss';

const rec = (r) => (r >= 4.5 ? { l: 'Scale', c: 'good' } : r >= 2.5 ? { l: 'Maintain', c: 'mid' } : { l: 'Review', c: 'low' });

export default function OverviewV4() {
  const navigate = useNavigate();
  const D = v4data(30);
  const { cur, prev, daily, dayLabels } = D;

  const kpis = [
    { label: 'Attributed Revenue', value: fmt$(cur.revenue), delta: pct(cur.revenue, prev.revenue), live: true, color: '#5A54F3', series: daily.map(d => d.totals.revenue) },
    { label: 'Ad Spend', value: fmt$(cur.adspend), delta: pct(cur.adspend, prev.adspend), deltaInvert: true, color: '#38bdf8', series: daily.map(d => d.totals.adspend) },
    { label: 'Blended ROAS', value: fmtX(cur.roas), delta: pct(cur.roas, prev.roas), color: '#8AE8BD', series: daily.map(d => d.totals.roas) },
    { label: 'Orders', value: fmtN(cur.orders), delta: pct(cur.orders, prev.orders), color: '#7A75F6', series: daily.map(d => d.totals.orders) },
    { label: 'Avg Order Value', value: fmt$2(cur.aov), delta: pct(cur.aov, prev.aov), color: '#f472b6', series: daily.map(d => d.totals.aov) },
    { label: 'Profit', value: fmt$(cur.profit), delta: pct(cur.profit, prev.profit), color: '#22d3ee', series: daily.map(d => d.totals.profit) },
  ];

  // revenue by channel/tier
  const tierTotals = {};
  D.ds.campaigns.forEach(c => { const m = D.campSum(c); tierTotals[c.tier] = (tierTotals[c.tier] || 0) + m.revenue; });
  const donutSegs = Object.keys(tierTotals).sort((a, b) => tierTotals[b] - tierTotals[a]).map(t => ({ label: TIER[t].label, color: TIER[t].color, value: tierTotals[t] }));
  const totalRev = donutSegs.reduce((a, s) => a + s.value, 0);

  // top campaigns by revenue
  const camps = D.ds.campaigns.map(c => ({ c, m: D.campSum(c) })).sort((a, b) => b.m.revenue - a.m.revenue);
  const maxRev = camps[0].m.revenue;

  // goal pace (monthly)
  const pace = (cur.revenue / GOALS.revenue) * 100;
  const projected = cur.revenue * 1.03;

  return (
    <div className="v4-dash">
      <PageHeader title="Overview" sub="Your whole account at a glance · Solène" live
        right={<><DatePill /><button className="v4-export">Export</button></>} />

      <MarkPopup inline context="overview" />

      <section className="v4-kpis">{kpis.map((k, i) => <StatCard key={i} {...k} />)}</section>

      {/* chart + donut */}
      <section className="v4-mid">
        <Card className="v4-chart-card" title="Revenue & Ad Spend" sub="Daily, trailing 30 days"
          right={<div className="v4-legend"><span><i style={{ background: '#5A54F3' }} /> Revenue</span><span><i style={{ background: '#38bdf8' }} /> Ad Spend</span></div>}>
          <AreaChart height={280} labels={dayLabels} series={[
            { name: 'Revenue', color: '#5A54F3', data: daily.map(d => d.totals.revenue) },
            { name: 'Ad Spend', color: '#38bdf8', data: daily.map(d => d.totals.adspend) },
          ]} />
        </Card>
        <div className="v4-side">
          <Card title="Revenue by Channel" className="v4-donut-card">
            <div className="v4-donut-wrap">
              <Donut segments={donutSegs} size={324} thickness={38} center={{ value: fmtK(totalRev), label: 'Revenue' }} />
              <ul className="v4-donut-legend">
                {donutSegs.map(s => <li key={s.label}><i style={{ background: s.color }} /><span className="nm">{s.label}</span><span className="vl">{Math.round(s.value / totalRev * 100)}%</span></li>)}
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* goal pace + top campaigns + MER + insight */}
      <section className="v4-grid3">
        <Card title="Monthly Goal Pace" sub="Store revenue vs target">
          <div className="v4-ring-wrap">
            <Ring pct={pace} size={188} thickness={15} label={pace.toFixed(0) + '%'} sub="OF TARGET" />
            <div className="v4-ring-stats">
              <div><span className="l">Target</span><span className="v">{fmtK(GOALS.revenue)}</span></div>
              <div><span className="l">Actual (30d)</span><span className="v pos">{fmtK(cur.revenue)}</span></div>
              <div><span className="l">Projected</span><span className="v" style={{ color: '#7A75F6' }}>{fmtK(projected)}</span></div>
            </div>
          </div>
        </Card>

        <Card title="Top Campaigns" sub="By attributed revenue · 30d">
          <ul className="v4-bar-list">
            {camps.slice(0, 5).map(({ c, m }) => (
              <li key={c.id}>
                <span className="nm">{c.name.split('—').pop().trim()}</span>
                <HBar value={m.revenue} max={maxRev} color={TIER[c.tier].color} w={120} />
                <span className="vl">{fmtK(m.revenue)}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Efficiency (MER)" sub="Blended · trailing 30 days">
          <div className="v4-mer-big"><span className="n">{cur.roas.toFixed(2)}x</span><span className={'v4-delta ' + (cur.roas >= GOALS.mer ? 'up' : 'down')}>{cur.roas >= GOALS.mer ? '▲ above' : '▼ below'} goal ({GOALS.mer}x)</span></div>
          <div className="v4-mer-spark"><Spark data={daily.map(d => d.totals.roas)} color="#8AE8BD" w={280} h={64} /></div>
        </Card>
      </section>

      {/* campaigns to watch */}
      <Card className="v4-table-card" pad={false}>
        <div className="v4-table-head"><h3 style={{ margin: 0 }}>Campaigns to Watch</h3><span className="v4-muted">Top 5 by revenue · 30d</span></div>
        <div className="v4-table">
          <div className="v4-tr v4-th"><span className="c-name">Campaign</span><span>Spend</span><span>Revenue</span><span>ROAS</span><span className="c-spark">Momentum</span><span className="c-rec">Action</span></div>
          {camps.slice(0, 5).map(({ c, m }) => {
            const rc = rec(m.roas), t = TIER[c.tier];
            const spark = D.campSeries(c, D.dates[Math.max(0, D.last - 13)], D.to).map(d => d.totals.roas);
            return (
              <div className="v4-tr v4-tr-sm" key={c.id}>
                <span className="c-name"><ChannelIcon color={t.color} /><span className="nm"><b>{c.name}</b><em style={{ color: t.color }}>{t.label}</em></span></span>
                <span>{fmt$2(m.adspend)}</span>
                <span className="pos">{fmt$2(m.revenue)}</span>
                <span className={'roas ' + rc.c}>{fmtX(m.roas)}</span>
                <span className="c-spark"><Spark data={spark} color={toneColor(rc.c)} w={92} h={26} fill={false} /></span>
                <span className="c-rec"><Pill tone={rc.c}>{rc.l}</Pill></span>
              </div>
            );
          })}
        </div>
        <button className="v4-seemore" onClick={() => navigate('/ads-manager')}>See all campaigns in Ads Manager →</button>
      </Card>
    </div>
  );
}
